import { useEffect, useState } from "react";
import { MessageSquare, Loader2, RefreshCcw, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { apiClient } from "@/lib/api";

export default function SmsPanel() {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    load();
  }, []);

  async function load() {
    setLoading(true);
    try {
      const { data } = await apiClient.get("/admin/reminders");
      setInfo(data);
    } catch (e) {
      toast.error("Could not load reminders info.");
    } finally {
      setLoading(false);
    }
  }

  async function runReminders() {
    if (!window.confirm("Send SMS reminders to every customer collecting today (that hasn't already been reminded)?")) return;
    setRunning(true);
    try {
      const { data } = await apiClient.post("/admin/reminders/run");
      const stubNote = data.sms_enabled ? "" : " (SMS provider not configured — logged only)";
      const sent = data.sent.length;
      const failed = data.failed.length;
      if (sent === 0 && failed === 0) {
        toast.info(`No pending reminders for today${stubNote}.`);
      } else {
        toast.success(`Sent ${sent}${failed ? ` · ${failed} failed` : ""}${stubNote}`);
      }
      load();
    } catch (e) {
      toast.error("Could not send reminders.");
    } finally {
      setRunning(false);
    }
  }

  return (
    <div data-testid="admin-sms-panel" className="space-y-6">
      <div className="border border-white/10 bg-[#0A0A0A] p-6 md:p-8">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#E60000] font-bold mb-2">
              SMS Reminders · Auto-scheduled
            </div>
            <h3 className="font-display text-2xl font-bold text-white">
              Daily 07:00 UK dispatch
            </h3>
            <p className="text-zinc-400 text-sm mt-2 max-w-2xl">
              Every morning at 07:00 (Europe/London) the scheduler texts every customer whose collection
              is scheduled for today and who hasn&rsquo;t already been reminded.
            </p>
          </div>
          <button
            data-testid="admin-sms-refresh"
            onClick={load}
            disabled={loading}
            className="inline-flex items-center gap-2 border border-white/15 px-4 py-2 text-xs tracking-[0.2em] uppercase font-bold hover:border-[#E60000] transition-colors disabled:opacity-50"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCcw className="w-4 h-4" />}
            Refresh
          </button>
        </div>

        {info && (
          <div className="grid sm:grid-cols-2 gap-4 mt-6">
            <StatCard
              label="Today (UK)"
              value={info.today}
              sublabel={`${info.count ?? 0} booking${(info.count ?? 0) === 1 ? "" : "s"} on schedule`}
            />
            <StatCard
              label="Next scheduled run"
              value={info.next_scheduled_run
                ? new Date(info.next_scheduled_run).toLocaleString("en-GB", { timeZone: "Europe/London" })
                : "—"}
              sublabel={info.sms_enabled ? "Twilio active" : "STUB mode — logs only"}
              warn={!info.sms_enabled}
            />
          </div>
        )}

        {info && !info.sms_enabled && (
          <div className="mt-6 flex items-start gap-3 border border-yellow-500/30 bg-yellow-500/5 p-4 text-xs text-yellow-300">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            <div>
              <div className="font-bold uppercase tracking-[0.2em]">Twilio not configured</div>
              <div className="mt-1 text-yellow-200/80">
                Add <code className="text-white">TWILIO_ACCOUNT_SID</code>,{" "}
                <code className="text-white">TWILIO_AUTH_TOKEN</code> and{" "}
                <code className="text-white">TWILIO_FROM_NUMBER</code> to <code className="text-white">/app/backend/.env</code>{" "}
                and restart backend to enable real SMS delivery.
              </div>
            </div>
          </div>
        )}

        <div className="mt-6 flex flex-wrap gap-3">
          <button
            data-testid="admin-run-reminders"
            onClick={runReminders}
            disabled={running}
            className="inline-flex items-center gap-2 bg-[#E60000] hover:bg-[#FF1A1A] px-6 py-3 text-xs tracking-[0.2em] uppercase font-bold text-white transition-colors disabled:opacity-50"
          >
            {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <MessageSquare className="w-4 h-4" />}
            Send today&rsquo;s reminders now
          </button>
        </div>
      </div>

      {info && (info.pending?.length > 0 || info.already_sent?.length > 0) && (
        <div className="grid md:grid-cols-2 gap-6">
          <BookingList
            title="Pending today"
            emptyMsg="No pending reminders."
            items={info.pending || []}
            testid="admin-sms-pending"
          />
          <BookingList
            title="Already reminded today"
            emptyMsg="No reminders sent today yet."
            items={info.already_sent || []}
            testid="admin-sms-sent"
            reminded
          />
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, sublabel, warn }) {
  return (
    <div className={`border ${warn ? "border-yellow-500/40" : "border-white/10"} bg-[#050505] p-4`}>
      <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-bold">{label}</div>
      <div className="text-white text-lg font-bold mt-1">{value || "—"}</div>
      {sublabel && <div className={`text-xs mt-1 ${warn ? "text-yellow-400" : "text-zinc-500"}`}>{sublabel}</div>}
    </div>
  );
}

function BookingList({ title, items, emptyMsg, testid, reminded }) {
  return (
    <div className="border border-white/10 bg-[#0A0A0A]" data-testid={testid}>
      <div className="px-4 py-3 border-b border-white/10 flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase font-bold text-zinc-400">
        {reminded && <CheckCircle2 className="w-3.5 h-3.5 text-[#E60000]" />}
        {title} · {items.length}
      </div>
      {items.length === 0 ? (
        <div className="px-4 py-6 text-center text-zinc-500 text-xs">{emptyMsg}</div>
      ) : (
        <ul className="divide-y divide-white/5">
          {items.map((b) => (
            <li key={b.booking_ref} className="px-4 py-3 text-sm flex items-center justify-between gap-3">
              <div>
                <div className="font-display font-bold text-white">{b.booking_ref}</div>
                <div className="text-xs text-zinc-500">{b.customer_name} · {b.customer_phone}</div>
              </div>
              <div className="text-xs text-zinc-400 text-right">
                <div>{b.requested_collection_time}</div>
                <div className="text-zinc-500 mt-0.5">{b.status?.replace(/_/g, " ")}</div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
